import { unwrap, type Api, type SessionUser } from "../api/index.js";

/**
 * The auth calls the sign-in flow makes, over the region's typed client.
 * A port so the step machine is testable without HTTP.
 */
export interface AuthApi {
  login(idToken: string): Promise<{ token: string; user: SessionUser }>;
  requestOtp(idToken: string): Promise<{ otpId: string }>;
  loginWithOtp(otpId: string, otp: string): Promise<{ token: string; user: SessionUser }>;
  signupWithOtp(otpId: string, otp: string): Promise<{ token: string; user: SessionUser }>;
  resendOtp(otpId: string): Promise<{ otpId: string }>;
  sendVerificationOtpEmail(email: string): Promise<{ otpId: string }>;
  /** POST /users/checkEmail: is there a KoverNow user for this email? */
  checkEmail(email: string): Promise<{ existed: boolean }>;
  /**
   * POST /users/registerAndLogin. Email sign-up → `otpId`; a verified
   * (Google/Apple) token → `token`. 409 `account_exists` when the email has a
   * user that this Firebase identity doesn't own.
   */
  registerAndLogin(input: RegisterInput): Promise<{ user: SessionUser; token?: string; otpId?: string }>;
  /** POST /users/checkPartnerCode (public): unknown/inactive → false. */
  checkPartnerCode(code: string): Promise<{ valid: boolean }>;
  /** POST /users/updatePassword: half-finished email sign-up recovery. */
  updatePassword(email: string, password: string): Promise<{ updated: boolean }>;
  sendResetPasswordOtpEmail(email: string): Promise<{ isSent: boolean; providerId: string; otpId?: string }>;
  /** Without a password: checks the code. With one: sets the new password. */
  resetWithOtp(input: { otpId: string; otp: string; password?: string }): Promise<{ isValidOtp: boolean }>;
  /** Revoke the refresh cookie's token and clear the cookie. */
  signOut(): Promise<void>;
  /** GET /serverStatus: "ok" | "maintenance" (406 forced-update is ignored on web). */
  serverStatus(): Promise<"ok" | "maintenance">;
}

export interface RegisterInput {
  /** Firebase uid. */
  id: string;
  name: string;
  familyName: string;
  email: string;
  /** From tenant config, never a literal. */
  orgId: string;
  firebaseIdToken: string;
  emailVerified?: boolean;
  marketingConsent: boolean;
  partnerCode?: string;
}

/** OTP request `uriScheme` (the backend uses it for app deeplinks only). */
const WEB_URI_SCHEME = "web";

function toUser(u: { _id: string; email: string; name?: string }): SessionUser {
  return { id: u._id, email: u.email, name: u.name };
}

export function createAuthApi(api: Api): AuthApi {
  const c = api.shared;
  return {
    async login(idToken) {
      const r = await unwrap(c.POST("/api/sg/users/login", { body: { idToken } }));
      return { token: r.data.token, user: toUser(r.data.user) };
    },
    async requestOtp(idToken) {
      const r = await unwrap(
        c.POST("/api/sg/users/otp", { body: { idToken, uriScheme: WEB_URI_SCHEME } }),
      );
      return { otpId: r.data.otpId };
    },
    async loginWithOtp(otpId, otp) {
      const r = await unwrap(c.POST("/api/sg/users/loginWithOtp", { body: { otpId, otp } }));
      return { token: r.data.token, user: toUser(r.data.user) };
    },
    async signupWithOtp(otpId, otp) {
      const r = await unwrap(c.POST("/api/sg/users/signupWithOtp", { body: { otpId, otp } }));
      return { token: r.data.token, user: toUser(r.data.user) };
    },
    async resendOtp(otpId) {
      const r = await unwrap(c.POST("/api/sg/users/resendOtp", { body: { otpId } }));
      return { otpId: r.data.otpId };
    },
    async sendVerificationOtpEmail(email) {
      const r = await unwrap(
        c.POST("/api/sg/users/sendVerificationOtpEmail", { body: { email } }),
      );
      return { otpId: r.data.otpId };
    },
    async checkEmail(email) {
      const r = await unwrap(c.POST("/api/sg/users/checkEmail", { body: { email } }));
      return { existed: r.data.existed };
    },
    async registerAndLogin(input) {
      const r = await unwrap(c.POST("/api/sg/users/registerAndLogin", { body: input }));
      return { user: toUser(r.data.user), token: r.data.token, otpId: r.data.otpId };
    },
    async checkPartnerCode(code) {
      const r = await unwrap(c.POST("/api/sg/users/checkPartnerCode", { body: { code } }));
      return { valid: r.data.valid };
    },
    async updatePassword(email, password) {
      const r = await unwrap(c.POST("/api/sg/users/updatePassword", { body: { email, password } }));
      return { updated: r.data.updated };
    },
    async sendResetPasswordOtpEmail(email) {
      const r = await unwrap(c.POST("/api/sg/users/sendResetPasswordOtpEmail", { body: { email } }));
      return { isSent: r.data.isSent, providerId: r.data.providerId, otpId: r.data.otpId };
    },
    async resetWithOtp(input) {
      const r = await unwrap(c.POST("/api/sg/users/resetWithOtp", { body: input }));
      return { isValidOtp: r.data.isValidOtp };
    },
    async signOut() {
      await unwrap(c.PUT("/api/sg/users/refreshToken", { body: {} }));
    },
    async serverStatus() {
      const { response } = await c.GET("/api/sg/serverStatus", {});
      return response.status === 503 ? "maintenance" : "ok";
    },
  };
}
